
import { Injectable } from '@angular/core';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';
import { Plan, AppUser } from '../models/index'; 

export interface PlatformStats {
  total_stores: number;
  active_subscriptions: number;
  total_mrr: number;
  total_patients: number;
  total_transactions_all_time: number;
}

export interface StoreSummary {
  id: string;
  name: string;
  created_at: string;
  plan_name: string;
  sub_status: string;
  store_status: 'active' | 'suspended'; 
  wallet_balance: number; 
  user_count: number;
}

export interface GlobalActivityLog {
    id: string;
    created_at: string;
    action_type: string;
    description: string;
    user_name: string;
    store_name: string;
    metadata?: any;
}

export interface PayoutStats {
    total_pending: number;
    pending_count: number;
    total_paid: number;
}

export interface PayoutLedgerItem {
    id: string;
    created_at: string;
    amount: number;
    status: 'PENDING' | 'PAID' | 'CANCELLED';
    payee_name: string;
    payee_id: string;
    payer_name: string;
    prescription_id: string;
    bank_account?: {
        bankName: string;
        accountNumber: string;
        accountName: string;
    };
    proof_url?: string;
    bank_ref_id?: string;
    paid_by_name?: string;
    paid_at?: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformService {
  private supabase: SupabaseClient | null = getSupabase();

  // --- Stats & Stores ---

  async getGlobalStats(): Promise<PlatformStats> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return {
            total_stores: 12,
            active_subscriptions: 10,
            total_mrr: 45000,
            total_patients: 1250,
            total_transactions_all_time: 5400
        };
    }
    const { data, error } = await this.supabase.rpc('get_platform_stats');
    if (error) throw error;
    return data as PlatformStats;
  }

  async getAllStores(): Promise<StoreSummary[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return [
            { id: '1', name: 'Bangkok Cannabis Hub', created_at: new Date().toISOString(), plan_name: 'Tier L', sub_status: 'active', store_status: 'active', wallet_balance: 5000, user_count: 5 },
        ];
    }
    const { data, error } = await this.supabase.rpc('get_all_stores_admin');
    if (error) throw error;
    return (data || []) as StoreSummary[];
  }

  // --- Platform Team Management ---

  async getPlatformTeam(): Promise<AppUser[]> {
      if (!this.supabase) return [];
      const { data, error } = await this.supabase.rpc('admin_get_platform_team');
      if (error) throw error;
      return (data || []) as AppUser[];
  }

  async invitePlatformStaff(email: string, role: string): Promise<string> {
      if (!this.supabase) throw new Error("DB not connected");
      const { data, error } = await this.supabase.rpc('admin_invite_platform_staff', {
          p_email: email,
          p_role: role
      });
      if (error) throw error;
      return data.token;
  }

  // --- Store Management Actions ---

  async extendTrial(storeId: string, days: number = 14): Promise<void> {
      if (!this.supabase) throw new Error("Database not connected");
      const { error } = await this.supabase.rpc('admin_extend_trial', { p_store_id: storeId, p_days: days });
      if (error) throw error;
  }

  async adjustStoreWallet(storeId: string, amount: number, description: string): Promise<void> {
      if (!this.supabase) throw new Error("Database not connected");
      const { error } = await this.supabase.rpc('admin_adjust_store_wallet', { 
          p_target_store_id: storeId, 
          p_amount: amount, 
          p_description: description 
      });
      if (error) throw error;
  }

  async toggleStoreStatus(storeId: string, newStatus: 'active' | 'suspended'): Promise<void> {
      if (!this.supabase) throw new Error("Database not connected");
      const { error } = await this.supabase.rpc('admin_toggle_store_status', { 
          p_target_store_id: storeId, 
          p_new_status: newStatus 
      });
      if (error) throw error;
  }

  // --- Plan Management ---

  async getPlans(): Promise<Plan[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.from('plans').select('*').order('price_monthly');
    if (error) throw error;
    return data as Plan[];
  }

  async updatePlan(plan: Plan): Promise<void> {
      if (!this.supabase) throw new Error("Database not connected");
      const { error } = await this.supabase.rpc('admin_update_plan', {
          p_plan_id: plan.id,
          p_price_monthly: plan.price_monthly,
          p_limits: plan.limits,
          p_features: plan.features
      });
      if (error) throw error;
  }

  // --- Global Logs ---
  async getGlobalLogs(filters: {
    startDate?: string,
    endDate?: string,
    storeId?: string,
    actionType?: string,
    pageSize: number,
    pageNumber: number
  }): Promise<{ logs: GlobalActivityLog[], total_count: number }> {
    if (!this.supabase) return { logs: [], total_count: 0 };
    const { data, error } = await this.supabase.rpc('admin_get_global_logs_paginated', {
        p_start_date: filters.startDate,
        p_end_date: filters.endDate,
        p_store_id: filters.storeId === 'all' ? null : filters.storeId,
        p_page_size: filters.pageSize,
        p_page_number: filters.pageNumber
    });

    if (error) throw error;
    const result = data as { logs: GlobalActivityLog[] | null, total_count: number | null };
    let logs = result.logs ?? [];
    if (filters.actionType && filters.actionType !== 'ALL') {
         logs = logs.filter(l => l.action_type.includes(filters.actionType!));
    }
    return { logs: logs, total_count: result.total_count ?? 0 };
  }

  // --- Backup ---
  async createFullBackup(): Promise<string> {
    if (!this.supabase) throw new Error("Database not connected.");
    const { data, error } = await this.supabase.rpc('admin_create_full_backup');
    if (error) throw error;
    return data as string;
  }

  // --- Clearing House ---
  
  async getPayoutStats(): Promise<PayoutStats> {
      if (!isSupabaseConfigured() || !this.supabase) {
          return { total_pending: 0, pending_count: 0, total_paid: 0 };
      }
      const { data, error } = await this.supabase.rpc('admin_get_payout_stats');
      if (error) throw error;
      return data as PayoutStats;
  }

  async getPayoutLedger(status: string = 'PENDING'): Promise<PayoutLedgerItem[]> {
      if (!isSupabaseConfigured() || !this.supabase) {
          return [];
      }
      const { data, error } = await this.supabase.rpc('admin_get_payout_ledger', { p_status: status, p_limit: 100 });
      if (error) throw error;
      return (data || []) as PayoutLedgerItem[];
  }

  async batchMarkPayoutPaid(ledgerIds: string[], proofUrl?: string, bankRefId?: string): Promise<void> {
    if (!this.supabase) throw new Error("Database not connected");
    const { error } = await this.supabase.rpc('admin_batch_mark_paid', { 
        p_ledger_ids: ledgerIds,
        p_proof_url: proofUrl,
        p_bank_ref_id: bankRefId
    });
    if (error) throw error;
  }

  async uploadPayoutSlip(file: File): Promise<string> {
    if (!this.supabase) throw new Error("Database not connected");
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.floor(Math.random()*10000)}.${fileExt}`;
    const filePath = `slips/${fileName}`;
    const { error: uploadError } = await this.supabase.storage.from('payout-evidence').upload(filePath, file);
    if (uploadError) throw uploadError;
    const { data: { publicUrl } } = this.supabase.storage.from('payout-evidence').getPublicUrl(filePath);
    return publicUrl;
  }
}
